/**
 * ParentGuide
 * Рекомендации для родителей — образование, хобби, общение, здоровье
 * Группа 5.4
 */

import React from 'react';

// ============================================================
// Стили
// ============================================================

const COLORS = {
  bg: '#0a0814',
  cardBg: 'rgba(30, 20, 50, 0.9)',
  cardBorder: 'rgba(233, 30, 99, 0.3)',
  pink: '#E91E63',
  purple: '#9C27B0',
  pinkLight: 'rgba(233, 30, 99, 0.15)',
  purpleLight: 'rgba(156, 39, 176, 0.15)',
  text: '#e0e0e0',
  textMuted: '#b0b0b0',
  blue: '#2196F3',
  green: '#4caf50',
  orange: '#ff9800',
  red: '#f44336',
};

// ============================================================
// Типы
// ============================================================

interface ParentGuideProps {
  advice: string[];
  educationTips: string[];
}

// ============================================================
// Компонент
// ============================================================

const ParentGuide: React.FC<ParentGuideProps> = ({ advice, educationTips }) => {
  const sectionStyle = (icon: string, title: string, items: string[], accentColor: string): React.ReactNode => (
    <div style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.cardBorder}`,
      borderRadius: '12px',
      padding: '20px',
    }}>
      <div style={{
        fontSize: '18px',
        fontWeight: 600,
        color: accentColor,
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <span style={{ fontSize: '22px' }}>{icon}</span>
        {title}
      </div>
      <ul style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        {items.map((item, i) => (
          <li key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            fontSize: '14px',
            lineHeight: 1.5,
            color: COLORS.text,
          }}>
            <span style={{
              flexShrink: 0,
              color: accentColor,
              fontWeight: 700,
              fontSize: '12px',
              marginTop: '2px',
            }}>•</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    }}>
      {/* Общие рекомендации */}
      {sectionStyle('👨‍👩‍👧', 'Рекомендации для родителей', advice, COLORS.pink)}

      {/* Образование */}
      {sectionStyle('📚', 'Советы по образованию', educationTips, COLORS.blue)}

      {/* Дополнительные секции с общими рекомендациями */}
      {sectionStyle('🎨', 'Хобби и творчество', [
        'Поощряйте творческие занятия — рисование, музыка, танцы',
        'Давайте ребёнку свободу выбирать, что ему интересно',
        'Создайте дома уголок для творчества',
        'Не навязывайте свои предпочтения — следите за интересами ребёнка',
      ], COLORS.purple)}

      {sectionStyle('💬', 'Общение и эмоциональное здоровье', [
        'Слушайте ребёнка внимательно, не прерывая',
        'Валидируйте его чувства — «я понимаю, что тебе грустно»',
        'Избегайте сравнений с другими детьми',
        'Создавайте традиции семейного общения',
      ], COLORS.green)}

      {sectionStyle('🏃', 'Физическое здоровье', [
        'Обеспечьте регулярную физическую активность',
        'Соблюдайте режим сна — дети нуждаются в 9-11 часах',
        'Ограничивайте экранное время',
        'Питайтесь вместе — это формирует здоровые привычки',
      ], COLORS.orange)}
    </div>
  );
};

export default ParentGuide;
